import { useEffect, useState } from "react";
import { HardDrive, CheckCircle2, AlertCircle, RefreshCw, Database, Cloud, Loader2, ArrowRight, FileText, Mail, Users, Car, Gavel } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const COLLECTIONS = [
  { key: "pec_inbox", label: "PEC Inbox (allegati)", icon: Mail, scan_field: "pec_inbox_with_b64" },
  { key: "rinotifiche", label: "Rinotifiche (PDF unito)", icon: Gavel, scan_field: "rinotifiche_with_package_b64" },
  { key: "contracts", label: "Contratti (PDF)", icon: FileText, scan_field: "contracts_with_pdf_b64" },
  { key: "customers", label: "Clienti (documenti)", icon: Users, scan_field: "customers_with_doc_b64" },
  { key: "cars", label: "Veicoli (foto)", icon: Car, scan_field: "cars_with_foto_b64" },
];

export default function StoragePage() {
  const [status, setStatus] = useState(null);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(null);
  const [results, setResults] = useState({});

  const load = async () => {
    try {
      const [s, sc] = await Promise.all([
        api.get("/storage/status"),
        api.get("/storage/migrate/scan"),
      ]);
      setStatus(s.data);
      setCounts(sc.data.counts || {});
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const migrate = async (coll, dry) => {
    setMigrating(coll);
    try {
      const r = await api.post("/storage/migrate", { collection: coll, dry_run: dry, limit: 200 });
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error("Nessun job_id");
      // Poll progress every 1.5s
      let job;
      while (true) {
        await new Promise((res) => setTimeout(res, 1500));
        const p = await api.get(`/storage/migrate/job/${jobId}`);
        job = p.data;
        // Live progress nel pannello
        setResults((prev) => ({ ...prev, [coll]: job }));
        if (job.status === "completed" || job.status === "failed") break;
      }
      if (job.status === "failed") {
        toast.error(`Migrazione fallita: ${job.fatal_error || "errore"}`);
      } else {
        toast.success(`${dry ? "DRY-RUN" : "MIGRATO"}: ${job.migrated} item · ${(job.bytes_moved/1024/1024).toFixed(1)} MB`);
      }
      if (!dry) await load();
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setMigrating(null); }
  };

  return (
    <div className="space-y-6" data-testid="storage-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Cloud className="text-orange-500"/> Cloud Storage (R2 / S3)
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Migrazione allegati base64 → Cloudflare R2 · solo URL/key in DB · CDN nativo · presigned URLs
          </p>
        </div>
        <button onClick={load} className="px-3 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm flex items-center gap-2 border border-zinc-700" data-testid="reload">
          <RefreshCw size={16}/> Aggiorna
        </button>
      </div>

      {/* Status banner */}
      {loading ? (
        <div className="text-center py-12 text-zinc-500"><Loader2 className="animate-spin inline mr-2"/></div>
      ) : status?.ok ? (
        <div className="p-5 rounded-xl bg-green-900/20 border border-green-700" data-testid="status-ok">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="text-green-400"/>
            <div className="font-semibold text-green-400">Cloudflare R2 connesso e operativo</div>
          </div>
          <div className="text-xs text-zinc-400 space-y-1">
            <div>Endpoint: <code className="text-white">{status.endpoint}</code></div>
            <div>Bucket: <code className="text-white">{status.bucket}</code></div>
            <div>Public URL base: <code className="text-white">{status.public_url_base}</code></div>
            {status.sample_objects?.length > 0 && (
              <div className="mt-2">Oggetti esempio: {status.sample_objects.length}</div>
            )}
          </div>
        </div>
      ) : (
        <div className="p-5 rounded-xl bg-amber-900/20 border border-amber-700" data-testid="status-missing">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="text-amber-400"/>
            <div className="font-semibold text-amber-400">Storage non ancora configurato</div>
          </div>
          {status?.missing?.length > 0 && (
            <div className="text-xs text-zinc-400">
              <div>Endpoint pre-impostato: <code className="text-white">{status.endpoint}</code></div>
              <div>Bucket pre-impostato: <code className="text-white">{status.bucket}</code></div>
              <div className="mt-2">
                <b className="text-amber-300">Mancano nelle variabili .env:</b>
                <ul className="list-disc pl-5 mt-1">
                  {status.missing.map(k => <li key={k}><code className="text-red-300">{k}</code></li>)}
                </ul>
              </div>
              <div className="mt-3 text-xs text-zinc-500">
                💡 Come ottenere le credenziali R2:<br/>
                1. Cloudflare Dashboard → R2 Object Storage → Manage R2 API Tokens<br/>
                2. Create API Token → Permission: Object Read & Write → Scope al bucket <code>03859111209</code><br/>
                3. Salva Access Key ID + Secret Access Key in <code>backend/.env</code>
              </div>
            </div>
          )}
          {status?.error && (
            <div className="text-xs text-red-300 mt-2">Errore: {status.error}</div>
          )}
        </div>
      )}

      {/* Counts grid */}
      <div>
        <h2 className="text-xs font-semibold uppercase text-zinc-500 mb-3">Documenti da migrare</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {COLLECTIONS.map(c => {
            const n = counts[c.scan_field] || 0;
            const Icon = c.icon;
            const result = results[c.key];
            return (
              <div key={c.key} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid={`coll-${c.key}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={18} className="text-orange-400"/>
                  <div className="font-semibold text-sm">{c.label}</div>
                </div>
                <div className="text-3xl font-bold mt-2">{n}</div>
                <div className="text-xs text-zinc-500 mt-1">documenti con base64</div>

                {result && (
                  <div className="mt-3 p-2 bg-zinc-950 rounded text-xs space-y-0.5">
                    <div className="flex items-center justify-between">
                      <span className={
                        result.status === 'running' ? "text-blue-400" :
                        result.status === 'completed' ? "text-green-400" :
                        result.status === 'failed' ? "text-red-400" : "text-zinc-400"
                      }>
                        {result.status === 'running' && <><Loader2 className="inline animate-spin mr-1" size={10}/>in corso</>}
                        {result.status === 'completed' && <>✓ completato</>}
                        {result.status === 'failed' && <>✗ fallito</>}
                        {result.status === 'queued' && <>⏳ in coda</>}
                      </span>
                      <span className="text-green-400">{result.migrated} migrati</span>
                    </div>
                    <div className="text-zinc-500">{(result.bytes_moved/1024/1024).toFixed(2)} MB spostati</div>
                    {result.errors > 0 && <div className="text-red-400">⚠ {result.errors} errori</div>}
                    {result.dry_run && <div className="text-amber-400 text-[10px]">DRY-RUN (no modifiche)</div>}
                    {result.fatal_error && <div className="text-red-300 text-[10px]">{result.fatal_error}</div>}
                  </div>
                )}

                <div className="flex gap-1 mt-3">
                  <button
                    onClick={() => migrate(c.key, true)}
                    disabled={migrating === c.key || n === 0 || !status?.ok}
                    className="flex-1 px-2 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 rounded disabled:opacity-30 flex items-center justify-center gap-1"
                    data-testid={`dry-${c.key}`}>
                    {migrating === c.key ? <Loader2 size={12} className="animate-spin"/> : "Test (dry-run)"}
                  </button>
                  <button
                    onClick={() => {
                      if (!window.confirm(`Migrare ${n} documenti reali su R2? Operazione irreversibile.`)) return;
                      migrate(c.key, false);
                    }}
                    disabled={migrating === c.key || n === 0 || !status?.ok}
                    className="flex-1 px-2 py-1.5 text-xs bg-orange-600 hover:bg-orange-500 rounded disabled:opacity-30 flex items-center justify-center gap-1 font-semibold"
                    data-testid={`migrate-${c.key}`}>
                    {migrating === c.key ? <Loader2 size={12} className="animate-spin"/> : <ArrowRight size={12}/>}
                    Migra
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Info benefici */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
        <div className="text-xs font-semibold uppercase text-zinc-500 mb-3">Vantaggi della migrazione</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
          <div className="p-3 bg-zinc-950 rounded">
            <div className="text-blue-400 font-bold">🚀 Performance</div>
            <div className="text-zinc-400 mt-1">MongoDB più veloce — documenti più piccoli</div>
          </div>
          <div className="p-3 bg-zinc-950 rounded">
            <div className="text-purple-400 font-bold">💾 Spazio</div>
            <div className="text-zinc-400 mt-1">No più limit 16MB per documento Mongo</div>
          </div>
          <div className="p-3 bg-zinc-950 rounded">
            <div className="text-green-400 font-bold">💰 Costi</div>
            <div className="text-zinc-400 mt-1">R2: 10GB gratis · no egress fees</div>
          </div>
          <div className="p-3 bg-zinc-950 rounded">
            <div className="text-amber-400 font-bold">🌐 CDN</div>
            <div className="text-zinc-400 mt-1">Cloudflare CDN integrato — globale</div>
          </div>
        </div>
      </div>
    </div>
  );
}
